<template>
  <section v-if="inited" id="slider" class="fixed bottom-0 left-0 right-0 top-0 touch-pan-y overflow-hidden bg-zinc-900">
    <div class="swiper-wrapper h-full flex items-center">
      <StoryItem v-for="story in stories.stories" :key="story.id" :story="story" />
    </div>

    <a class="absolute right-1 top-5 h-12 w-12 opacity-70 transition sm:right-4 sm:top-4 sm:opacity-50 lt-sm:p-3 hover:opacity-70" href="\">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 11 11">
        <path
          fill="#fff"
          d="M2.2 1.19l3.3 3.3L8.8 1.2a.67.67 0 0 1 .5-.2a.75.75 0 0 1 .7.7a.66.66 0 0 1-.2.48L6.49 5.5L9.8 8.82c.13.126.202.3.2.48a.75.75 0 0 1-.7.7a.67.67 0 0 1-.5-.2L5.5 6.51L2.21 9.8a.67.67 0 0 1-.5.2a.75.75 0 0 1-.71-.71a.66.66 0 0 1 .2-.48L4.51 5.5L1.19 2.18A.66.66 0 0 1 1 1.7a.75.75 0 0 1 .7-.7a.67.67 0 0 1 .5.19z"
        />
      </svg>
    </a>

    <button
      data-button-prev
      class="absolute left-4 top-1/2 h-12 w-12 flex rounded-full bg-white opacity-50 transition-all flex-center lt-sm:hidden -translate-y-1/2 hover:opacity-70"
      :class="hideNavPrev && 'invisible'"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24">
        <path
          fill="#000"
          d="M15.125 21.1L6.7 12.7q-.15-.15-.213-.325T6.425 12q0-.2.062-.375T6.7 11.3l8.425-8.425q.35-.35.875-.35t.9.375q.375.375.375.875t-.375.875L9.55 12l7.35 7.35q.35.35.35.863t-.375.887q-.375.375-.875.375t-.875-.375Z"
        />
      </svg>
    </button>

    <button
      data-button-next
      class="absolute right-4 top-1/2 h-12 w-12 flex rounded-full bg-white opacity-50 transition-all flex-center lt-sm:hidden -translate-y-1/2 hover:opacity-70"
    >
      <svg v-if="!changeIconNavNext" xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24">
        <path
          d="M7.15 21.1q-.375-.375-.375-.888t.375-.887L14.475 12l-7.35-7.35q-.35-.35-.35-.875t.375-.9q.375-.375.888-.375t.887.375l8.4 8.425q.15.15.213.325T17.6 12q0 .2-.063.375t-.212.325L8.9 21.125q-.35.35-.863.35T7.15 21.1Z"
        />
      </svg>

      <svg v-else xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 8.467 8.467" class="-scale-100">
        <path
          d="M6.74 2.99a.397.397 0 0 0-.37.52c.072.234.109.48.109.725a2.434 2.434 0 0 1-2.956 2.384.398.398 0 1 0-.168.778 3.24 3.24 0 0 0 3.771-4.121.397.397 0 0 0-.386-.286zM4.037.998A3.242 3.242 0 0 0 .943 5.18a.397.397 0 1 0 .76-.233 2.436 2.436 0 0 1 2.334-3.154c.242 0 .483.035.715.106a.397.397 0 1 0 .232-.758 3.236 3.236 0 0 0-.947-.143z"
        />
        <path
          d="M1.316 4.66a.397.397 0 0 0-.373.52 3.236 3.236 0 0 0 2.412 2.217.398.398 0 1 0 .168-.778 2.438 2.438 0 0 1-1.82-1.672.397.397 0 0 0-.387-.287zM6.234 2.322a.397.397 0 0 0-.369.293l-.271 1.018a.397.397 0 0 0 .582.447l1.29-.746a.397.397 0 0 0-.095-.726l-1.02-.274a.397.397 0 0 0-.117-.012z"
        />
      </svg>
    </button>

    <SwiperPagination />
  </section>

  <Transition
    enter-active-class="transition-all ease-out"
    enter-from-class="transform opacity-0"
    enter-to-class="transform opacity-100"
    leave-active-class="transition-all ease-out"
    leave-from-class="transform opacity-100"
    leave-to-class="transform opacity-0"
  >
    <div v-if="!inited" class="absolute left-0 top-0 h-screen w-screen bg-white">
      <UiPreloader />
    </div>
  </Transition>
</template>

<script setup lang="ts">
import Swiper from "swiper";
import { Autoplay, Navigation, Pagination } from "swiper/modules";

Swiper.use([Autoplay, Navigation, Pagination]);

const stories = getStories();

const inited = ref(false);

const hideNavPrev = ref(true);
const changeIconNavNext = ref(false);

const initSwiper = () => {
  return new Swiper("#slider", {
    centeredSlides: true,
    slideToClickedSlide: true,
    rewind: true,

    autoplay: {
      delay: 15_000,
      stopOnLastSlide: true,
      disableOnInteraction: false,
    },

    navigation: {
      prevEl: "[data-button-prev]",
      nextEl: "[data-button-next]",
    },

    pagination: {
      el: "#pagination",
      clickable: true,
    },

    breakpoints: {
      415: {
        slidesPerView: "auto",
      },
      320: {
        slidesPerView: 1,
      },
    },

    on: {
      slideChange({ realIndex, slides }) {
        hideNavPrev.value = realIndex === 0;
        changeIconNavNext.value = realIndex === slides.length - 1;
      },
    },
  });
};

onMounted(async () => {
  await useAppInit();
  inited.value = true;
  await nextTick();
  initSwiper();
});
</script>

<style>
html {
  --at-apply: font-sans;
  -webkit-tap-highlight-color: transparent;
}
</style>
